import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Settings, Plus, Search, Trash2, Pencil, Layers, Sprout, ChevronLeft, ArrowRight, X, CheckCircle2, Scale,
} from "lucide-react";
import api from "../lib/api";
import { Card, Badge, Button, PageHeader, PageLoader, Modal } from "../lib/ui";
import { ProductAvatar } from "../components/visual";
import { useToast } from "../lib/toast";
import { useT } from "../lib/i18n";

const CLASS_LABEL = { RTC: "Bahan Siap Masak", SEMI_FINISHED: "Barang Setengah Jadi", FINISHED: "Produk Jadi" };

export default function PrepareConfig() {
  const { t } = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // {source_product_id?} for modal

  const load = useCallback(async () => {
    const { data } = await api.get("/prepare/config");
    setData(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const removeConfig = async (cfg) => {
    if (!window.confirm(t("Delete this configuration?"))) return;
    try { await api.delete(`/prepare/config/${cfg.source_product_id}`); toast.success(t("Configuration deleted")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };

  if (!data) return <PageLoader />;

  const q = search.trim().toLowerCase();
  const configs = data.configs.filter((c) =>
    !q || (c.source_name || "").toLowerCase().includes(q) || (c.outputs || []).some((o) => (o.output_name || "").toLowerCase().includes(q)));
  const totalOutputs = data.configs.reduce((s, c) => s + (c.output_count || 0), 0);

  return (
    <div className="animate-fade-up pb-24 lg:pb-2" data-testid="prepare-config">
      <button onClick={() => navigate("/meja-prepare")} className="mb-3 inline-flex items-center gap-1 text-sm font-bold text-primary" data-testid="pc-back"><ChevronLeft size={16} /> {t("Back to Meja Prepare")}</button>
      <PageHeader icon={Settings} title={t("Preparation Configuration")} subtitle={t("Map each raw material to its possible prepared outputs")}>
        <Button onClick={() => setEditing({})} data-testid="pc-add-btn"><Plus size={16} /> {t("Add Configuration")}</Button>
      </PageHeader>

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="pc-summary">
        <Card className="p-4"><div className="flex items-center gap-3"><span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600"><Sprout size={20} /></span><div><p className="font-heading text-2xl font-extrabold text-ink">{data.configs.length}</p><p className="text-sm font-bold text-ink">{t("Configured Raw Materials")}</p></div></div></Card>
        <Card className="p-4"><div className="flex items-center gap-3"><span className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-50 text-violet-600"><Layers size={20} /></span><div><p className="font-heading text-2xl font-extrabold text-ink">{totalOutputs}</p><p className="text-sm font-bold text-ink">{t("Possible Outputs")}</p></div></div></Card>
        <Card className="p-4"><div className="flex items-center gap-3"><span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-50 text-amber-600"><Layers size={20} /></span><div><p className="font-heading text-2xl font-extrabold text-ink">{data.prepared_products.length}</p><p className="text-sm font-bold text-ink">{t("Prepared Products")}</p></div></div></Card>
      </div>

      <div className="mb-4 relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="pc-search"
          placeholder={t("Search raw material or output…")} className="w-full rounded-xl border border-line bg-white py-2.5 pl-9 pr-3 text-sm focus:border-primary focus:outline-none" />
      </div>

      {configs.length === 0 ? (
        <Card className="p-10 text-center text-ink-mute" data-testid="pc-empty">
          {t("No configuration yet")}
          <div className="mt-3"><Button variant="outline" onClick={() => setEditing({})} data-testid="pc-empty-add"><Plus size={15} /> {t("Add Configuration")}</Button></div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="pc-list">
          {configs.map((c) => (
            <Card key={c.id} className="flex flex-col p-4" data-testid={`pc-config-row-${c.source_product_id}`}>
              <div className="flex items-start gap-3">
                <ProductAvatar image={c.source_image} colorKey={c.source_name} name={c.source_name} size="md" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-heading text-base font-extrabold text-ink">{c.source_name}</p>
                  <p className="text-xs text-ink-mute">{c.output_count} {t("possible outputs")} · {c.source_unit}</p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <button onClick={() => setEditing({ source_product_id: c.source_product_id })} data-testid={`pc-edit-${c.source_product_id}`}
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-ink-soft transition hover:border-primary hover:text-primary"><Pencil size={15} /></button>
                  <button onClick={() => removeConfig(c)} data-testid={`pc-delete-${c.source_product_id}`}
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-ink-soft transition hover:border-danger hover:text-danger-text"><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(c.outputs || []).map((o) => (
                  <span key={o.output_product_id} className="inline-flex items-center gap-1 rounded-full bg-app px-2.5 py-1 text-xs font-semibold text-ink-soft" data-testid={`pc-output-chip-${o.output_product_id}`}>
                    {o.output_name}
                    {o.expected_yield_pct != null && <span className="text-[10px] text-emerald-600">{o.expected_yield_pct}%</span>}
                  </span>
                ))}
                {(c.outputs || []).length === 0 && <span className="text-xs text-ink-mute">{t("No outputs configured")}</span>}
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && <ConfigModal data={data} initialSource={editing.source_product_id} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ConfigModal({ data, initialSource, onClose, onSaved }) {
  const { t } = useT();
  const toast = useToast();
  const isEdit = !!initialSource;
  const existing = useMemo(() => data.configs.find((c) => c.source_product_id === initialSource), [data, initialSource]);

  const [sourceId, setSourceId] = useState(initialSource || "");
  const [outSearch, setOutSearch] = useState("");
  const [sel, setSel] = useState(() => {
    const m = {};
    (existing?.outputs || []).forEach((o) => { m[o.output_product_id] = { checked: true, yield: o.expected_yield_pct ?? "", conv: o.conversion_factor ?? 1 }; });
    return m;
  });
  const [busy, setBusy] = useState(false);

  // Raw materials already configured (other than the one being edited) — block to keep 1 raw per config.
  const configuredSources = new Set(data.configs.filter((c) => c.source_product_id !== initialSource).map((c) => c.source_product_id));
  const rawOptions = data.raw_materials.filter((r) => !configuredSources.has(r.id));
  const sourceUnit = useMemo(() => {
    const r = data.raw_materials.find((x) => x.id === (initialSource || sourceId));
    return r?.unit || existing?.source_unit || "kg";
  }, [data, initialSource, sourceId, existing]);
  const [baseUnit, setBaseUnit] = useState(existing?.base_unit || "");
  const [inputFactor, setInputFactor] = useState(existing?.input_factor ?? 1);
  const effBase = baseUnit || sourceUnit;

  const toggle = (p) => setSel((s) => {
    const cur = s[p.id];
    if (cur?.checked) { const n = { ...s }; delete n[p.id]; return n; }
    return { ...s, [p.id]: { checked: true, yield: p.expected_yield_pct ?? "", conv: 1 } };
  });
  const setYield = (id, v) => setSel((s) => ({ ...s, [id]: { ...s[id], yield: v } }));
  const setConv = (id, v) => setSel((s) => ({ ...s, [id]: { ...s[id], conv: v } }));

  const selectedCount = Object.values(sel).filter((v) => v?.checked).length;

  const q = outSearch.trim().toLowerCase();
  const outList = data.prepared_products.filter((p) => !q || (p.name || "").toLowerCase().includes(q));

  const save = async () => {
    if (!sourceId) { toast.error(t("Select a raw material")); return; }
    if (selectedCount === 0) { toast.error(t("Select at least one output")); return; }
    setBusy(true);
    try {
      const outputs = Object.entries(sel).filter(([, v]) => v?.checked).map(([id, v]) => ({
        output_product_id: id, expected_yield_pct: v.yield === "" || v.yield == null ? null : Number(v.yield),
        conversion_factor: v.conv === "" || v.conv == null ? 1 : Number(v.conv),
      }));
      await api.post("/prepare/config", { source_product_id: sourceId, outputs, base_unit: baseUnit || null, input_factor: Number(inputFactor) || 1 });
      toast.success(t("Configuration saved"));
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={isEdit ? `${t("Edit Configuration")}` : t("Add Configuration")} size="lg"
      footer={<div className="flex items-center justify-between gap-3">
        <span className="text-xs text-ink-mute">{selectedCount} {t("outputs selected")}</span>
        <Button onClick={save} loading={busy} disabled={!sourceId || selectedCount === 0} data-testid="pc-save-btn"><CheckCircle2 size={16} /> {t("Save Configuration")}</Button>
      </div>}>
      <div className="space-y-4" data-testid="pc-modal">
        <label className="block">
          <span className="text-xs font-semibold text-ink-soft">{t("Raw Material")}</span>
          {isEdit ? (
            <div className="mt-1 rounded-xl border border-line bg-app px-3 py-2.5 text-sm font-bold text-ink">{existing?.source_name}</div>
          ) : (
            <select value={sourceId} onChange={(e) => setSourceId(e.target.value)} data-testid="pc-source-select"
              className="mt-1 w-full rounded-xl border border-line bg-white py-2.5 px-3 text-sm focus:border-primary focus:outline-none">
              <option value="">{t("Select a raw material")}</option>
              {rawOptions.map((r) => <option key={r.id} value={r.id}>{r.name}{r.category_name ? ` · ${r.category_name}` : ""}</option>)}
            </select>
          )}
        </label>

        <div className="rounded-xl border border-line bg-app p-3" data-testid="pc-conversion">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-ink-mute"><Scale size={13} /> {t("Unit Conversion (optional)")}</p>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] font-semibold text-ink-soft">{t("Base unit (for yield)")}</span>
              <input value={baseUnit} onChange={(e) => setBaseUnit(e.target.value)} data-testid="pc-base-unit" placeholder={sourceUnit}
                className="mt-1 w-full rounded-lg border border-line py-1.5 px-2.5 text-sm focus:border-primary focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold text-ink-soft">1 {sourceUnit} = ? {effBase}</span>
              <input type="number" min="0" step="0.01" value={inputFactor} onChange={(e) => setInputFactor(e.target.value)} data-testid="pc-input-factor"
                className="mt-1 w-full rounded-lg border border-line py-1.5 px-2.5 text-sm font-bold focus:border-primary focus:outline-none" />
            </label>
          </div>
          <p className="mt-1.5 text-[11px] text-ink-mute">{t("Use when purchase/stock unit differs from the yield unit (e.g. 1 ekor = 1.2 kg). Leave as 1 if same.")}</p>
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-xs font-semibold text-ink-soft">{t("Possible Outputs")} ({t("Bahan Siap Masak")})</span>
          </div>
          <div className="relative mb-2">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute" />
            <input value={outSearch} onChange={(e) => setOutSearch(e.target.value)} data-testid="pc-out-search"
              placeholder={t("Search output…")} className="w-full rounded-xl border border-line bg-white py-2 pl-9 pr-3 text-sm focus:border-primary focus:outline-none" />
          </div>
          <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
            {outList.length === 0 && <p className="py-4 text-center text-xs text-ink-mute">{t("No prepared products available")}</p>}
            {outList.map((p) => {
              const on = !!sel[p.id]?.checked;
              return (
                <div key={p.id} className={`rounded-xl border p-2.5 transition ${on ? "border-primary bg-primary-light/50" : "border-line bg-white"}`} data-testid={`pc-output-${p.id}`}>
                  <div className="flex items-center gap-2.5">
                    <button type="button" onClick={() => toggle(p)} className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${on ? "border-primary bg-primary text-white" : "border-line"}`} data-testid={`pc-output-toggle-${p.id}`}>
                      {on && <CheckCircle2 size={13} />}
                    </button>
                    <button type="button" onClick={() => toggle(p)} className="min-w-0 flex-1 text-left">
                      <p className="truncate text-sm font-bold text-ink">{p.name}</p>
                      <p className="text-[10px] text-ink-mute">{p.unit} · {CLASS_LABEL[p.production_class] || p.production_class}{p.in_config && !on ? ` · ${t("in another config")}` : ""}</p>
                    </button>
                    <Badge tone={p.production_class === "RTC" ? "success" : "warn"}>{CLASS_LABEL[p.production_class] || p.production_class}</Badge>
                    {on && (
                      <div className="flex shrink-0 items-center gap-2">
                        <div className="flex items-center gap-1" title={t("Expected yield %")}>
                          <input type="number" min="0" max="100" step="1" value={sel[p.id]?.yield ?? ""} data-testid={`pc-yield-${p.id}`}
                            onChange={(e) => setYield(p.id, e.target.value)} placeholder={t("Yield")}
                            className="w-14 rounded-lg border border-line py-1 px-2 text-right text-sm font-bold focus:border-primary focus:outline-none" />
                          <span className="text-xs text-ink-mute">%</span>
                        </div>
                        {(p.unit !== effBase) && (
                          <div className="flex items-center gap-1" title={`1 ${p.unit} = ? ${effBase}`}>
                            <input type="number" min="0" step="0.01" value={sel[p.id]?.conv ?? 1} data-testid={`pc-conv-${p.id}`}
                              onChange={(e) => setConv(p.id, e.target.value)}
                              className="w-14 rounded-lg border border-line py-1 px-2 text-right text-sm focus:border-primary focus:outline-none" />
                            <span className="text-[10px] text-ink-mute">{p.unit}/{effBase}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-2 text-[11px] text-ink-mute">{t("Yield % is the expected portion of raw that becomes this output (optional, for review flags).")}</p>
        </div>
      </div>
    </Modal>
  );
}
